"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function PipeMarkingPlantSchemes() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const features = [{
    number: "S.01",
    title: "Industrial Compliance",
    description: "Engineered to meet strict ANSI/ASME standards for facility identification and safety protocols."
  }, {
    number: "S.02",
    title: "High-Durability Materials",
    description: "UV-resistant, chemical-proof substrates designed to withstand extreme industrial environments."
  }, {
    number: "S.03",
    title: "Precision Color Coding",
    description: "Standardized color schemes for fluid identification, ensuring rapid recognition and operational safety."
  }, {
    number: "S.04",
    title: "Custom Plant Schematics",
    description: "Bespoke labeling solutions tailored to your facility's unique piping infrastructure and layout."
  }];
  return <section data-section-id="2480" id="pipe-marking-plant-schemes" className="bg-background text-foreground py-40 px-6 md:px-12 lg:px-20 overflow-hidden">
      <div className="max-w-[1440px] mx-auto">
        <div className="grid grid-cols-12 gap-6 md:gap-10">
          {}
          <div ref={headerRef} className={`col-span-12 lg:col-span-8 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="flex items-center gap-3 mb-10">
              <span className="inline-block w-2 h-2 bg-primary"></span>
              <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                01 / Industrial Identification
              </span>
            </div>

            <h2 className="font-default font-black leading-[0.92] tracking-tight mb-10" style={{
            fontSize: "clamp(3rem, 7vw, 6rem)"
          }}>
              Where industrial
              <br />
              <span className="text-muted-foreground">safety meets</span>
              <br />
              precision.
            </h2>

            <p className="text-lg max-w-[540px] text-muted-foreground leading-relaxed">
              Our facility marking systems are where regulatory compliance meets operational clarity. Every label, 
              every schematic, every color-coded marker is engineered against your facility's specific 
              infrastructure — because a safe plant is only as effective as the information that guides it.
            </p>
          </div>

          {}
          <div ref={imageRef} className={`col-span-12 lg:col-span-4 transition-all duration-1000 ease-out ${imageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <div className="relative w-full overflow-hidden rounded-lg shadow-lg" style={{
            aspectRatio: "3 / 5"
          }}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/industrial_pipe_marking_installation.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/industrial_pipe_marking_installation.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/industrial_pipe_marking_installation.webp-wvc-sizes" alt="Technician installing industrial pipe markers in a manufacturing facility" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
              {}
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-transparent pointer-events-none"></div>
              {}
              <div className="absolute inset-0 opacity-20 mix-blend-overlay pointer-events-none" style={{
              backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")"
            }}></div>
              {}
              <div className="absolute bottom-6 left-6 right-6 flex justify-between items-end font-mono text-[10px] uppercase tracking-widest text-foreground/80">
                <span>Plant / Systems</span>
                
              </div>
            </div>
          </div>
        </div>

        {}
        <div ref={gridRef} className="grid grid-cols-12 gap-6 md:gap-10 mt-24">
          <div className="col-span-12 lg:col-span-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-24">
              {features.map((feature, i) => <div data-index={i} key={feature.number} className={`group relative transition-all duration-700 ease-out ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
              transitionDelay: gridVisible ? `${i * 120}ms` : "0ms"
            }}>
                  <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground block mb-4">
                    {feature.number}
                  </span>
                  <h3 className="font-default text-2xl font-semibold mb-3 leading-tight">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground text-base leading-relaxed mb-5 max-w-[380px]">
                    {feature.description}
                  </p>
                  <span className="block h-px w-12 bg-primary transition-all duration-500 ease-out group-hover:w-32"></span>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}